/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { useMemo, useState } from 'react'

import type { EuiTableSortingType, Pagination } from '@elastic/eui'
import { Comparators } from '@elastic/eui'

import type { CloudResource } from '@modules/billing-api/v1/types'

import { BudgetsTableField } from '../types'

import { balanceComparator, scopeComparator } from './utils'

import type { Budget, Costs } from '../types'

const INITIAL_PAGE_INDEX = 0
const PAGE_SIZE = 5

type Props = {
  budgets: Budget[] | undefined
  cloudResources: CloudResource[] | undefined
  costs: Costs | undefined
}

const useTableContent = ({
  budgets,
  cloudResources,
  costs,
}: Props): {
  pagination: Pagination | undefined
  pageOfItems: Budget[]
  sorting: EuiTableSortingType<Budget>
  handleTableChange: (page: unknown) => void
} => {
  const [pageIndex, setPageIndex] = useState(INITIAL_PAGE_INDEX)
  const [sortField, setSortField] = useState<BudgetsTableField>(BudgetsTableField.NAME)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const sortedBudgets = useMemo(() => {
    switch (sortField) {
      case BudgetsTableField.SCOPE:
        return budgets?.slice(0).sort((a, b) => scopeComparator(a, b, sortDirection))

      case BudgetsTableField.BALANCE:
        if (!costs) {
          return budgets
        }

        return budgets
          ?.slice(0)
          .sort((a, b) => balanceComparator({ a, b, cloudResources, costs, sortDirection }))

      // by default, columns whose assigned `field` corresponds to a budget field are compared
      default:
        return budgets
          ?.slice(0)
          .sort(Comparators.property(sortField, Comparators.default(sortDirection)))
    }
  }, [sortField, budgets, cloudResources, costs, sortDirection])

  const sorting = {
    sort: {
      field: sortField,
      direction: sortDirection,
    },
    enableAllColumns: true,
  } as EuiTableSortingType<Budget>

  const handleTableChange = ({ page, sort }) => {
    if (page) {
      const { index: newPageIndex } = page
      setPageIndex(newPageIndex)
    }

    if (sort) {
      const { field: newField, direction: newDirection } = sort

      if (newField === 'balance' && !costs) {
        return
      }

      setSortField(newField)
      setSortDirection(newDirection)
    }
  }

  const totalItemCount = sortedBudgets?.length ?? 0
  const startIndex = pageIndex * PAGE_SIZE
  const pageOfItems = sortedBudgets?.slice(
    startIndex,
    Math.min(startIndex + PAGE_SIZE, totalItemCount),
  )

  const hasPaginationEnabled = totalItemCount > PAGE_SIZE

  const pagination = hasPaginationEnabled
    ? {
        pageIndex,
        pageSize: PAGE_SIZE,
        totalItemCount,
        pageSizeOptions: [],
        showPerPageOptions: false,
      }
    : undefined

  return {
    pagination,
    pageOfItems: pageOfItems || [],
    sorting,
    handleTableChange,
  }
}

export default useTableContent
